import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { KeyRound, Save, UserCircle, Mail, Building2, Lock } from "lucide-react";

type ProfileFormData = {
  firstName: string;
  lastName: string;
};

type ChangePasswordFormData = {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

async function updateProfile(data: ProfileFormData): Promise<any> {
  const response = await fetch("/api/auth/profile", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error("Failed to update profile");
  }

  return response.json();
}

async function changePassword(data: { oldPassword: string; newPassword: string }): Promise<{ message: string }> {
  const response = await fetch("/api/auth/password", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || "Failed to update password");
  }

  return response.json();
}

function IconInput({
  icon: Icon,
  ...props
}: { icon: React.ElementType } & React.ComponentProps<typeof Input>) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
      <Input {...props} className={`pl-10 rounded-2xl ${props.className || ""}`} />
    </div>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: "",
    lastName: "",
  });

  const [passwordForm, setPasswordForm] = useState<ChangePasswordFormData>({
    oldPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  useEffect(() => {
    if (!user) return;
    setFormData({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
    });
  }, [user]);

  const profileMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Update failed",
        description: "Failed to update your profile. Please try again.",
        variant: "destructive",
      });
    },
  });

  const passwordMutation = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      setPasswordForm({ oldPassword: "", newPassword: "", confirmNewPassword: "" });
      toast({ title: "Password updated" });
    },
    onError: (e: Error) => {
      toast({ title: "Password update failed", description: e.message, variant: "destructive" });
    },
  });

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    profileMutation.mutate(formData);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmNewPassword) {
      toast({ title: "Missing fields", description: "Please fill in all password fields.", variant: "destructive" });
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast({ title: "Password too short", description: "New password must be at least 6 characters.", variant: "destructive" });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
      toast({ title: "Passwords don't match", description: "Confirm password must match the new password.", variant: "destructive" });
      return;
    }

    passwordMutation.mutate({ oldPassword: passwordForm.oldPassword, newPassword: passwordForm.newPassword });
  };

  const handleChange = (field: keyof ProfileFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handlePasswordChange = (field: keyof ChangePasswordFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPasswordForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  if (!user) {
    return null;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-10 px-6">
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-4xl font-extrabold tracking-tight text-primary">
          {"Profile Settings"}
        </h1>
        <p className="text-lg text-muted-foreground">
          {"Manage your personal information"}
        </p>
      </div>

      <div className="space-y-8">
        {/* General Information Card */}
        <div className="bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
          <div className="px-6 py-5 border-b border-border/30 flex items-center gap-3">
            <UserCircle className="h-4 w-4 text-primary" />
            <span className="text-lg font-semibold text-foreground">
              {"General Information"}
            </span>
          </div>

          <form onSubmit={handleProfileSubmit} className="px-12 py-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="firstName">{"First Name"}</Label>
                <IconInput
                  icon={UserCircle}
                  id="firstName"
                  value={formData.firstName}
                  onChange={handleChange("firstName")}
                  placeholder={"Enter your first name"}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{"Last Name"}</Label>
                <IconInput
                  icon={UserCircle}
                  id="lastName"
                  value={formData.lastName}
                  onChange={handleChange("lastName")}
                  placeholder={"Enter your last name"}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{"Email Address"}</Label>
              <IconInput
                icon={Mail}
                id="email"
                value={user.email}
                disabled
                className="bg-muted cursor-not-allowed"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyName">{"Company Name"}</Label>
              <IconInput
                icon={Building2}
                id="companyName"
                value={user.companyName || ""}
                disabled
                className="bg-muted cursor-not-allowed"
                placeholder="—"
              />
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                disabled={profileMutation.isPending}
                className="rounded-2xl px-5 py-2.5"
              >
                {profileMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {"Saving..."}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {"Save Changes"}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* Password Card */}
        <div className="bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
          <div className="px-6 py-5 border-b border-border/30 flex items-center gap-3">
            <KeyRound className="h-4 w-4 text-primary" />
            <span className="text-lg font-semibold text-foreground">
              {"Password"}
            </span>
          </div>

          <form onSubmit={handlePasswordSubmit} className="px-12 py-6 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="oldPassword">{"Current Password"}</Label>
              <IconInput
                icon={Lock}
                id="oldPassword"
                type="password"
                autoComplete="current-password"
                value={passwordForm.oldPassword}
                onChange={handlePasswordChange("oldPassword")}
                placeholder={"Enter your current password"}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="newPassword">{"New Password"}</Label>
                <IconInput
                  icon={Lock}
                  id="newPassword"
                  type="password"
                  autoComplete="new-password"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange("newPassword")}
                  placeholder={"Enter a new password"}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmNewPassword">{"Confirm New Password"}</Label>
                <IconInput
                  icon={Lock}
                  id="confirmNewPassword"
                  type="password"
                  autoComplete="new-password"
                  value={passwordForm.confirmNewPassword}
                  onChange={handlePasswordChange("confirmNewPassword")}
                  placeholder={"Re-enter the new password"}
                />
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                disabled={passwordMutation.isPending}
                className="rounded-2xl px-5 py-2.5"
              >
                {passwordMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {"Updating..."}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {"Update Password"}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
